// src/pages/CompanyDefaults/components/ViewModalHeader.jsx
import React from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import ExpandIcon from "@mui/icons-material/OpenInFullOutlined";
import ShrinkIcon from "@mui/icons-material/CloseFullscreenOutlined";
import ModeIcon from "@mui/icons-material/ModeOutlined"; // Pencil Icon
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'; // Save Icon
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";

const ViewModalHeader = ({
  isExpanded,
  setIsExpanded,
  handleCloseModal,
  isEditMode = false,
  handleEditButtonClick, // Function to *enter* edit mode
  handleCancel,          // Potentially redundant now, but kept for consistency
  handleDelete,
  handleSave,            // Function to *save* changes (triggers PUT)
  handleOpenAddModal,
  isSaving = false,
  autosaveStatus = "",
}) => {
  return (
    <div className="w-full">
      <div className="relative flex items-center justify-between px-4 py-2">   

        {/* Center Action Icons */}
        <div className="absolute left-1/2 -translate-x-1/2 flex space-x-4">
          {/* EDIT / SAVE Button */}
          <Tooltip title={isEditMode ? "Save Changes" : "Edit"}>
            <button
              onClick={() => {
                if (isEditMode) {
                  // If already editing, clicking this icon triggers SAVE
                  console.log("[Header] Save icon clicked => handleSave()");
                  handleSave && handleSave();
                } else {
                  // If not editing, clicking this icon enters EDIT mode
                  console.log("[Header] Edit icon clicked => handleEditButtonClick('edit')");
                  handleEditButtonClick && handleEditButtonClick("edit");
                }
              }}
              className={`p-2 rounded-full hover:bg-gray-300 transition ${
                isEditMode ? "border border-sss-info" : "" // Highlight border when editing
              }`}
              disabled={isSaving} // Disable button while saving
            >
              {/* Show Save icon when editing, Pencil icon otherwise */}
              {isEditMode ? (
                <SaveOutlinedIcon
                  className={isSaving ? "text-gray-400" : "text-sss-info"} // Dim icon if saving
                />
              ) : (
                <ModeIcon className="text-gray-700" />
              )}
            </button>
          </Tooltip>

          {/* Add Button */}
          <Tooltip title="Add">
            <button
              onClick={handleOpenAddModal}
              className="p-2 rounded-full hover:bg-gray-300 transition"
              disabled={isEditMode || isSaving} // Disable if editing/saving main form
            >
              <AddIcon className={(isEditMode || isSaving) ? "text-gray-400" : "text-gray-700"} />
            </button>
          </Tooltip>

          {/* Delete Button */}
          <Tooltip title="Delete">
            <button
              onClick={handleDelete}
              className="p-2 rounded-full hover:bg-gray-300 active:text-sss-info transition"
              aria-label="Delete"
              disabled={isEditMode || isSaving} // Disable if editing/saving main form
            >
              <DeleteIcon className={`hover:text-red-500 ${(isEditMode || isSaving) ? "text-gray-400" : "text-gray-700"}`} />
            </button>
          </Tooltip>
        </div>

        {/* Right Side Icons & Status */}
        <div className="flex items-center space-x-2">
          {/* Autosave Status */}
          {isEditMode && (
            <div className="mr-2 text-sm text-gray-500">
              {isSaving ? "Saving..." : autosaveStatus || ""} {/* Clear "Saved" text unless specified */}
            </div>
          )}
          {/* Open in New Window */}
          <Tooltip title="Open in New Window">
            <button
              className="p-2 hover:bg-gray-300 transition"
              aria-label="Open in New Window"
              disabled={isEditMode || isSaving}
            >
              <LaunchOutlinedIcon className={(isEditMode || isSaving) ? "text-gray-400" : "text-gray-700"} />
            </button>
          </Tooltip>
         
        </div>
      </div>
    </div>
  );
};

ViewModalHeader.propTypes = {
  isExpanded: PropTypes.bool,
  setIsExpanded: PropTypes.func,
  handleCloseModal: PropTypes.func,
  isEditMode: PropTypes.bool,
  handleEditButtonClick: PropTypes.func, // Function to ENTER edit mode
  handleCancel: PropTypes.func,          // Function to explicitly cancel (might be unused if pencil doesn't cancel anymore)
  handleDelete: PropTypes.func,
  handleSave: PropTypes.func.isRequired, // Function to SAVE changes (now required for the icon logic)
  handleOpenAddModal: PropTypes.func,
  isSaving: PropTypes.bool,
  autosaveStatus: PropTypes.string,
};

export default ViewModalHeader;